import { DbotEventDispatcher, DecisionContext } from "@/dbot/common/commonDomain";
import { CompanyRole } from "@/types/CompanyInfo";
import { getFullName, sortedBoard } from "@/utils/Roles";
import { newUuid } from "@/utils/uuid";
import { makeAutoObservable } from "mobx";
import { RoleType } from "@/types/enums/RoleType";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel, SelectableWithIdModel, StringFieldModel } from "@/common/commonForms";
import { SelectableWithId } from "@/common/common";
import { isNorwegianOrgNo } from "@/utils/orgNo";

export type NewBoardMember = {
  name: string;
  id: string; //uuid
};

type SelectedBoardMember = {
  name?: string;
  id?: string; // uuid or number/companyRoleId
};

export class SelectBoardMemberFieldModel {
  readonly params: { value?: SelectedBoardMember; required?: boolean };

  value?: SelectedBoardMember;
  error?: TMessage = undefined;

  constructor(params: { value?: SelectedBoardMember; required?: boolean } = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value = params.value;
  }

  setValue(value?: SelectedBoardMember) {
    this.value = value;
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate(): boolean {
    if (this.params.required && this.value) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
    return this.isValid;
  }
}

export type DeputyFor = {
  id?: string;
  name?: string;
};

export type SelectableDeputy = {
  id: string;
  name?: string;
  isSelected?: boolean;
  isPersonal?: boolean;
  personalDeputyFor?: Array<DeputyFor>;
};

export class SelectableDeputyModel {
  readonly params: { value?: Array<SelectableDeputy>; required?: boolean };

  value: Array<SelectableDeputy>;
  error?: TMessage = undefined;

  constructor(params: { value?: Array<SelectableDeputy>; required?: boolean } = {}) {
    makeAutoObservable(this, { params: false }, { autoBind: true });
    this.params = params;
    this.value = params.value ?? [];
  }

  get selectedIds(): string[] {
    return this.value
      .filter((it) => {
        return it.isSelected;
      })
      .map((it) => it.id);
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate() {
    if (this.params.required && this.value.length === 0) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
  }

  add(item: SelectableDeputy) {
    this.value.push(item);
  }

  toggleSelected(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isSelected = item.isSelected === undefined ? true : !item.isSelected;
    }
  }

  togglePersonalDeputy(id: string) {
    const item = this.getById(id);
    if (item) {
      item.isPersonal = item.isPersonal === undefined ? true : !item.isPersonal;
    }
  }

  getById(id: string): SelectableDeputy | undefined {
    return this.value.find((x) => x.id === id);
  }

  togglePersonalDeputyFor(id: string, deputyFor: DeputyFor) {
    const item = this.getById(id);
    if (item) {
      if (item.personalDeputyFor?.find((d) => d.id === deputyFor.id)) {
        item.personalDeputyFor = item.personalDeputyFor.filter((d) => d.id !== deputyFor.id);
      } else {
        item.personalDeputyFor?.push(deputyFor);
      }
    }
  }

  selectAll() {
    this.value.forEach((value) => (value.isSelected = true));
  }
}

export type AssignsNewCompanyBoard = {
  type: DecisionType.assignsNewCompanyBoard;

  members: Array<NewBoardMember>;
  membersByName: Array<NewBoardMember>;
  selectChairperson?: boolean;
  chairperson?: SelectedBoardMember;
  selectDeputyChairperson?: boolean;
  deputyChairperson?: SelectedBoardMember;
  deputy?: Array<SelectableDeputy>;
  deputyByName?: Array<SelectableDeputy>;
  boardMeetingProtocolShowCompanyBoard: boolean;
  noticeGeneralMeetingShowCompanyBoard: boolean;
  genderCompositionRequirement?: boolean;
};

export class AssignsNewCompanyBoardForm {
  readonly type = DecisionType.assignsNewCompanyBoard;

  newBoardMembers: SelectableWithIdModel;
  newBoardMembersByName: SelectableWithIdModel;
  selectChairPerson: BooleanFieldModel;
  selectedChairperson: SelectBoardMemberFieldModel;
  selectDeputyChairperson: BooleanFieldModel;
  selectedDeputyChairperson: SelectBoardMemberFieldModel;
  newDeputyBoardMembers: SelectableDeputyModel;
  newDeputyBoardMembersByName: SelectableDeputyModel;
  boardMeetingProtocolShowCompanyBoard: BooleanFieldModel;
  noticeGeneralMeetingShowCompanyBoard: BooleanFieldModel;

  addNewBoardMemberModel?: { name: StringFieldModel };
  addNewBoardDeputyModel?: { name: StringFieldModel };
  genderCompositionRequirement: BooleanFieldModel;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    private context: DecisionContext<AssignsNewCompanyBoard>,
    public data?: AssignsNewCompanyBoard,
  ) {
    this.newBoardMembers = new SelectableWithIdModel({
      value: this.roles
        .filter((r) =>
          [RoleType.CHAIRPERSON, RoleType.DEPUTY_CHAIRPERSON, RoleType.MEMBER].includes(r.role),
        )
        .map((r) => ({
          name: getFullName(r),
          id: r.companyroleId.toString(),
          isSelected: false,
        })),
    });
    if (data?.members) {
      data?.members.forEach((m) => this.newBoardMembers.select(m?.id));
    } else {
      this.newBoardMembers.selectAll();
    }

    const namedMembers = data?.membersByName.map((m) => ({
      id: m.id,
      name: m.name,
      isSelected: true,
    }));
    this.newBoardMembersByName = new SelectableWithIdModel({ value: namedMembers });
    this.selectChairPerson = new BooleanFieldModel({
      value: data?.selectChairperson ?? true,
    });
    this.selectedChairperson = new SelectBoardMemberFieldModel({
      value: data?.chairperson
        ? data.chairperson
        : this.makeSelectableForRole(this.roles, RoleType.CHAIRPERSON),
    });

    this.selectDeputyChairperson = new BooleanFieldModel({
      value: data?.selectDeputyChairperson ?? false,
    });
    this.selectedDeputyChairperson = new SelectBoardMemberFieldModel({
      value: data
        ? data.deputyChairperson
        : this.makeSelectableForRole(this.roles, RoleType.DEPUTY_CHAIRPERSON),
    });

    this.newDeputyBoardMembers = new SelectableDeputyModel({
      value: this.roles
        .filter((r) => [RoleType.DEPUTY_MEMBER].includes(r.role))
        .map((r) => ({
          name: getFullName(r),
          id: r.companyroleId.toString(),
          isSelected: false,
          isPersonal: false,
          personalDeputyFor: [],
        })),
    });
    if (data?.deputy) {
      data?.deputy?.forEach((m) => {
        this.newDeputyBoardMembers.toggleSelected(m.id);
        if (m.isPersonal) {
          this.newDeputyBoardMembers.togglePersonalDeputy(m.id);
          m.personalDeputyFor?.forEach((d) => {
            this.newDeputyBoardMembers.togglePersonalDeputyFor(m.id, d);
          });
        }
      });
    } else {
      this.newDeputyBoardMembers.selectAll();
    }

    const namedDeputyMembers = data?.deputyByName?.map((m) => ({
      id: m.id,
      name: m.name,
      isSelected: true,
      isPersonal: m.isPersonal,
      personalDeputyFor: m.personalDeputyFor,
    }));
    this.newDeputyBoardMembersByName = new SelectableDeputyModel({ value: namedDeputyMembers });

    this.boardMeetingProtocolShowCompanyBoard = new BooleanFieldModel({
      value: data?.boardMeetingProtocolShowCompanyBoard ?? false,
    });
    this.noticeGeneralMeetingShowCompanyBoard = new BooleanFieldModel({
      value: data?.noticeGeneralMeetingShowCompanyBoard ?? false,
    });
    this.genderCompositionRequirement = new BooleanFieldModel({
      value: data?.genderCompositionRequirement,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  private makeSelectableForRole(roles: CompanyRole[], roleType: RoleType) {
    const role = roles.find((r) => r.role === roleType);
    if (!role) {
      return;
    }
    return {
      name: getFullName(role),
      id: role.companyroleId.toString(),
    };
  }

  get showNoBoardMembersSelectedText() {
    return (
      this.newBoardMembers && this.newBoardMembersByName && this.numberOfSelectedBoardMembers === 0
    );
  }

  get showSingleBoardMemberText() {
    return (
      this.newBoardMembers && this.newBoardMembersByName && this.numberOfSelectedBoardMembers === 1
    );
  }

  get numberOfSelectedBoardMembers() {
    return this.selectableBoardMembers.length;
  }

  get showSelectChairpersonQuestion() {
    return (
      this.newBoardMembers && this.newBoardMembersByName && this.numberOfSelectedBoardMembers > 1
    );
  }

  get showSelectChairPersonSelect() {
    return (
      this.selectChairPerson.value &&
      this.newBoardMembers &&
      this.newBoardMembersByName &&
      this.numberOfSelectedBoardMembers > 1
    );
  }

  get showSelectDeputyChairpersonQuestion() {
    return (
      this.selectChairPerson.value &&
      this.selectDeputyChairperson.value &&
      this.selectedChairperson.value &&
      this.newBoardMembers.value &&
      this.newBoardMembersByName.value &&
      this.numberOfSelectedBoardMembers > 1
    );
  }

  get isChairpersonAmongSelectable() {
    return this.selectableBoardMembers.some((m) => m.id === this.selectedChairperson.value?.id);
  }

  get isDeputyChairpersonAmongSelectable() {
    return this.selectableBoardMembersExceptChairperson.some(
      (m) => m.id === this.selectedDeputyChairperson.value?.id,
    );
  }

  get selectableBoardMembers(): SelectedBoardMember[] {
    const selectedNewBoardMembers = this.newBoardMembers.value.filter((m) => m.isSelected);
    const selectedNewBoardMembersByName = this.newBoardMembersByName.value.filter(
      (m) => m.isSelected,
    );
    return selectedNewBoardMembers.concat(selectedNewBoardMembersByName);
  }

  get selectableBoardMembersExceptChairperson(): SelectedBoardMember[] {
    return this.selectableBoardMembers.filter((m) => m.id !== this.selectedChairperson.value?.id);
  }

  get orgNo() {
    return this.context.orgNo;
  }

  get isNorwegianOrgNo() {
    return isNorwegianOrgNo(this.orgNo);
  }

  get roles(): CompanyRole[] {
    return sortedBoard(this.context.initialState.roles);
  }

  get rolesMissingSsn() {
    const rolesWithMissingSsn = this.roles.filter(
      (d) => d.socialSecurityNumber === undefined || d.socialSecurityNumber === "",
    );
    return rolesWithMissingSsn.filter(
      (role) =>
        this.newBoardMembers.selectedIds.includes(role.companyroleId.toString()) ||
        this.newDeputyBoardMembers.selectedIds.includes(role.companyroleId.toString()),
    );
  }

  get deputyMembersWithMissingSsn() {
    return this.rolesMissingSsn.filter((r) => [RoleType.DEPUTY_MEMBER].includes(r.role));
  }

  get boardMembersWithMissingSsn() {
    return this.rolesMissingSsn.filter((r) =>
      [RoleType.CHAIRPERSON, RoleType.DEPUTY_CHAIRPERSON, RoleType.MEMBER].includes(r.role),
    );
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid() {
    return (
      this.numberOfSelectedBoardMembers > 0 &&
      this.newBoardMembers.isValid &&
      this.newBoardMembersByName.isValid &&
      this.selectChairPerson.isValid &&
      this.selectedChairperson.isValid &&
      this.selectDeputyChairperson.isValid &&
      this.selectedDeputyChairperson.isValid &&
      this.newDeputyBoardMembers.isValid &&
      this.newDeputyBoardMembersByName.isValid
    );
  }

  validate(): boolean {
    this.newBoardMembers.validate();
    this.newBoardMembersByName.validate();
    this.selectChairPerson.validate();
    this.selectedChairperson.validate();
    this.selectDeputyChairperson.validate();
    this.selectedDeputyChairperson.validate();
    this.newDeputyBoardMembers.validate();
    this.newDeputyBoardMembersByName.validate();
    return this.isValid;
  }

  openAddNewBoardMemberDialog() {
    this.addNewBoardMemberModel = {
      name: new StringFieldModel({ required: true }),
    };
  }

  closeAddNewBoardMemberDialog() {
    this.addNewBoardMemberModel = undefined;
  }

  addNewBoardMember() {
    if (this.addNewBoardMemberModel) {
      this.addNewBoardMemberModel.name.commit();
      if (this.addNewBoardMemberModel.name.isValid) {
        const boardSelected = this.newBoardMembers.value.some((v) => v.isSelected);
        const byNameSelected = this.newBoardMembersByName.value.some((v) => v.isSelected);
        const newMember: SelectableWithId = {
          id: newUuid(),
          name: this.addNewBoardMemberModel.name.validatedValue,
          isSelected: true,
        };
        this.newBoardMembersByName.add(newMember);
        if (!boardSelected && !byNameSelected) {
          this.selectedChairperson.setValue(newMember);
        }
        this.closeAddNewBoardMemberDialog();
      }
    }
  }

  openAddNewBoardDeputyDialog() {
    this.addNewBoardDeputyModel = {
      name: new StringFieldModel({ required: true }),
    };
  }

  closeAddNewBoardDeputyDialog() {
    this.addNewBoardDeputyModel = undefined;
  }

  addNewBoardDeputy() {
    if (this.addNewBoardDeputyModel) {
      this.addNewBoardDeputyModel.name.commit();
      if (this.addNewBoardDeputyModel.name.isValid) {
        this.newDeputyBoardMembersByName.add({
          id: newUuid(),
          name: this.addNewBoardDeputyModel.name.validatedValue,
          isSelected: true,
          isPersonal: false,
          personalDeputyFor: [],
        });
        this.closeAddNewBoardDeputyDialog();
      }
    }
  }

  toggleNewBoardMember(id: string): void {
    this.newBoardMembers.toggle(id);

    // show select chairperson dropdown if existing chairperson is removed from board
    if (!this.newBoardMembers.isSelected(id)) {
      if (this.isChairperson(id)) {
        this.selectChairPerson.setValue(true);
      }
    }
    this.setChairpersonIfNotValid();
    this.setDeputyChairpersonIfNotValid();
  }

  toggleNewBoardMemberByName(id: string): void {
    this.newBoardMembersByName.toggle(id);
    this.setChairpersonIfNotValid();
    this.setDeputyChairpersonIfNotValid();
  }

  setDeputyChairpersonIfNotValid() {
    if (!this.isDeputyChairpersonAmongSelectable) {
      this.selectedDeputyChairperson.setValue(this.selectableBoardMembersExceptChairperson[0]);
    }
  }

  setChairpersonIfNotValid() {
    if (this.numberOfSelectedBoardMembers === 1) {
      this.selectedChairperson.setValue(this.selectableBoardMembers[0]);
    } else if (!this.isChairpersonAmongSelectable) {
      this.selectedChairperson.setValue(this.selectableBoardMembers[0]);
    }
  }

  private isChairperson(id: string) {
    const role = this.roles.find((r) => r.companyroleId.toString() === id);
    return role?.role === RoleType.CHAIRPERSON;
  }
}
